import { useTranslation } from '@/lib/i18n';
import { cn } from '@worldcoo/ui/lib/utils';
import { Image } from '@/routes/-components/image';
import { PhotoPlaceholder } from '../primitives';
import type { Milestone } from './milestones-data';

/**
 * A shallow `0.25` first handle, since a zoom starting from rest reads as lag after the triggering scroll.
 * 500ms matches the travel below so both arrive together; at 700ms the size still settled after the slide stopped.
 */
const SLIDE = 'duration-500 ease-[cubic-bezier(0.25,0.1,0.25,1)]';

function MilestoneArtwork(props: { image: NonNullable<Milestone['image']> }) {
	const { language } = useTranslation();
	const image = props.image;

	if (image.kind === 'illustration') {
		// Lifted 9px of the card's 318 (47.2%); the SVG's own ratio makes it overhang the top edge.
		return (
			<img
				src={image.src}
				alt={image.alt}
				style={{ width: image.width }}
				className="pointer-events-none absolute top-[47.2%] left-1/2 h-auto -translate-x-1/2 -translate-y-1/2"
			/>
		);
	}

	if (image.kind === 'screen') {
		// Anchored to the corner: the capture runs off the right and bottom edges by design.
		return (
			<img
				src={image.src}
				alt={image.alt}
				style={{ width: image.width }}
				className="pointer-events-none absolute right-0 bottom-0 h-auto"
			/>
		);
	}

	return (
		<Image
			src={typeof image.src === 'string' ? image.src : (image.src[language] ?? image.src.en)}
			alt={image.alt}
			height={318}
			aspectRatio={442 / 318}
			objectFit="cover"
			className="pointer-events-none size-full object-cover!"
		/>
	);
}

/**
 * Two notions of active, because a multi-card year is one slide at `xl` and one swipe per card below it:
 * base classes follow `isFocused`, the `xl:` layer `inFocusedYear`, and the pull `yearOffset`, so two cards
 * of one year never pull towards each other.
 */
export function MilestoneCard(props: {
	milestone: Milestone;
	isFocused: boolean;
	inFocusedYear: boolean;
	yearOffset: number;
	onSelect: () => void;
}) {
	const image = props.milestone.image;

	return (
		/* Travel on its own element: it answers to the track's ~350ms smooth scroll, and on the zoom's curve the card
		   overshot the centre by 34px and drifted back. */
		<div
			className={cn(
				'w-full transition-[translate] duration-500 ease-[cubic-bezier(0.22,1,0.36,1)] xl:w-[442px]',
				// The export's pull (774.5px, not the 843px step). A translate animates through zero; a flipped
				// transform-origin would jump the card 137px in one frame.
				props.yearOffset < 0 && 'xl:translate-x-[68.5px]',
				props.yearOffset > 0 && 'xl:-translate-x-[68.5px]',
			)}
		>
			{/* Not a button: the rail is the control, and a button would double every year in the tab order. */}
			<div
				className={cn(
					// `scale`, not `transform`: Tailwind writes the individual property, so `transform` transitions nothing.
					'flex w-full flex-col gap-[9px] transition-[opacity,scale] xl:origin-[50%_159px]',
					SLIDE,
					props.isFocused ? 'opacity-100' : 'cursor-pointer opacity-25',
					// One scale on the parent: two about different origins pulled card and caption apart mid-transition.
					// The origin is the card's centre, half of its 318px.
					!props.inFocusedYear && 'xl:scale-[0.69]',
					!props.isFocused && props.inFocusedYear && 'xl:cursor-default xl:opacity-100',
				)}
				onClick={props.isFocused ? undefined : props.onSelect}
			>
				<div
					className={cn(
						// Pure layout: only the slide's scale animates, so the card cannot drift from its caption.
						'relative aspect-[442/318] w-full shrink-0 overflow-hidden rounded-3xl',
						// The hand-off's plate (see the hero's app card); a capture sits on dark so the UI reads.
						image?.kind === 'screen' ? 'bg-ink' : 'bg-[#FFEDDF]',
						'xl:aspect-auto xl:h-[318px] xl:w-[442px]',
					)}
				>
					{/* No `label`: a msgid for scaffolding would cost ten translations. */}
					{image ? <MilestoneArtwork image={image} /> : <PhotoPlaceholder className="size-full" />}
				</div>
				{/* No scale of its own: the slide's transform keeps it welded to its card. */}
				{props.milestone.caption && (
					<p className="text-ink w-full text-base leading-[1.5] tracking-[-0.02em] xl:text-xl">
						{props.milestone.caption}
					</p>
				)}
			</div>
		</div>
	);
}
